import { useContext, useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import styled from 'styled-components'
import dayjs from 'dayjs'

import { PARAM_FILTER, PARAM_SORT_KEYS, PARAM_SORT } from '../../constants'
import type { PARAM_SORT_TYPE } from '../../constants'
import type { components } from '../../constants/schema'

import { HttpReq, toast } from '../../utils'

import { StoresContext } from '../../provider/StoreProvider'

import { Wrap } from '../../components/container/Wrap'
import { Content } from '../../components/container/Content'
import { AlignCenterItem, ColumnItem, RowBetween } from '../../components/common'
import { BankIcon } from '../../components/bankIcon'
import { Button } from '../../components/button'
import { Text } from '../../components/text'
import { DivideLine } from '../../components/divide'
import { Header } from '../../components/header'
import { ParamOption, Sort } from '../../components/paramOption'
import { Spinner } from '../../components/spinner/Spinner'

import { Color } from '../../theme/color'
import type { ColorKeys } from '../../theme/color'

const FILTER_LIST = [
  { type: undefined, title: '전체' },
  { type: PARAM_FILTER.SALE, title: '청약 신청' },
  { type: PARAM_FILTER.DEPOSIT, title: '예치금 충전' },
  { type: PARAM_FILTER.WITHDRAWAL, title: '예치금 출금' },
  { type: PARAM_FILTER.SETTLEMENT, title: '매각 대금' },
  { type: PARAM_FILTER.UNALLOCATED, title: '환불' },
  { type: PARAM_FILTER.REFUND, title: '청약 취소' },
]

const SORT_LIST = [
  { type: PARAM_SORT_KEYS.DESC, title: '최근 거래순' },
  { type: PARAM_SORT_KEYS.ASC, title: '오래된 거래순' },
]

export const Account = observer(() => {
  const { commonStore } = useContext(StoresContext)

  const [assetSummary, setAssetSummary] = useState<components['schemas']['ResUserAssetSummary'] | null>(null)
  const [depositAccount, setDepositAccount] = useState<components['schemas']['DepositAccountDto'] | null>(null)
  const [accountPages, setAccountPages] = useState<components['schemas']['CashTransactionLogDto'][]>([])
  const [sortType, setSortType] = useState<PARAM_SORT_TYPE>(PARAM_SORT_KEYS.DESC)
  const [filterType, setFilterType] = useState<PARAM_FILTER | undefined>()
  const [isLoading, setIsLoading] = useState<boolean>(true)

  useEffect(() => {
    commonStore.setShowTab(false)

    getAssetSummary()
    getDepositAccount()
    return () => {
      sessionStorage.removeItem('pageTab')
    }
  }, [])

  useEffect(() => {
    getAccountPages({ sortType, filterType, filterName: 'transactionType' })
  }, [sortType, filterType])

  useEffect(() => {
    if (assetSummary && depositAccount) {
      setIsLoading(false)
    }
  }, [assetSummary, depositAccount, accountPages])

  const getAssetSummary = async () => {
    try {
      const response = await HttpReq<components['schemas']['ResUserAssetSummary']>({
        method: 'GET',
        url: '/user/current/assetSummary',
      })
      setAssetSummary(response)
    } catch (error) {
      console.log('getAssetSummary error: ', { error })
      toast('예치금 금액을 불러오는데 실패했어요.', error)
    }
  }

  const getDepositAccount = async () => {
    try {
      const response = await HttpReq<components['schemas']['DepositAccountDto']>({
        method: 'GET',
        url: '/user/current/depositAccount',
      })
      setDepositAccount(response)
    } catch (error) {
      console.log('getDepositAccount error: ', { error })
      toast('예치금 계좌 정보를 불러오는데 실패했어요.', error)
    }
  }

  const getAccountPages = async ({
    sortType,
    filterType,
    filterName,
  }: {
    sortType?: PARAM_SORT_TYPE
    filterType?: string
    filterName?: string
  }) => {
    try {
      const sort = sortType ? PARAM_SORT[sortType] : undefined
      const response = await HttpReq<components['schemas']['ResListCashTransactionLog']>({
        method: 'GET',
        url: '/user/current/cash/transaction',
        data: { sort, [filterName || 'transactionType']: filterType, size: 150 },
      })
      setAccountPages(response.list)
    } catch (error) {
      console.log('getAccountPages error: ', { error })
      toast('입출금 내역을 불러오는데 실패했어요.', error)
    }
  }

  return (
    <Wrap>
      <Header type="SUB" title="입출금 내역" />
      <StyledContent>
        {isLoading ? (
          <LoadingWrap>
            <Spinner />
          </LoadingWrap>
        ) : (
          <>
            <AccountInfoWrap>
              <AccountCard>
                <RowBetween>
                  <AlignCenterItem $gap={8}>
                    <BankIcon name="NH농협은행" size={24} />
                    <Text size={16} weight={500} lineHeight={24}>
                      NH농협은행
                    </Text>
                  </AlignCenterItem>
                  <Text size={14} lineHeight={22} color="text_secondary">
                    {depositAccount?.account}
                  </Text>
                </RowBetween>
                <DivideLine />
                <UserAccountInfo>
                  <Text size={14} lineHeight={22}>
                    <Text size={14} weight={600}>
                      {depositAccount?.bankOwner}님
                    </Text>
                    의 예치금
                  </Text>
                  <Text size={24} weight={600} lineHeight={34} color="primary_5">
                    {Number(assetSummary?.depositBalance || 0).toLocaleString()}원
                  </Text>
                </UserAccountInfo>
                <AlignCenterItem $gap={8}>
                  <Button type="secondary" size="small" block={true} href="/myPage/account/withdrawal">
                    출금하기
                  </Button>
                  <Button type="primary" size="small" block={true} href="/myPage/account/deposit">
                    충전하기
                  </Button>
                </AlignCenterItem>
              </AccountCard>
            </AccountInfoWrap>
            <FilterContainer>
              <ParamOption slideFilterData={{ filterType, setFilterType, filterList: FILTER_LIST }} />
            </FilterContainer>
            <SortContainer>
              <Sort sortType={sortType} sortList={SORT_LIST} setSortType={setSortType} />
            </SortContainer>
            {accountPages.length ? (
              <ColumnItem>
                {accountPages.map(
                  ({ id, createdDate, transactionType, purchaseTypeLabel, orderSequence, amount, balance, hasFee }) => {
                    const YMD = dayjs(createdDate).format('YY.MM.DD')
                    const HM = dayjs(createdDate).format('HH:mm')

                    const isWithdrawal = String(amount).includes('-')

                    const textColor: ColorKeys = isWithdrawal ? 'primary_5' : 'error'

                    return (
                      <BreakdownContainer key={id}>
                        <TransactionContainer>
                          <ColumnItem $gap={6}>
                            <Text size={10} color="text_secondary">
                              {YMD}
                            </Text>
                            <Text size={10} color="text_secondary">
                              {HM}
                            </Text>
                          </ColumnItem>
                          <ColumnItem $gap={4}>
                            <RowBetween>
                              <Text size={16} weight={500}>
                                {purchaseTypeLabel}
                              </Text>
                              <ColumnItem>
                                <RowBetween>
                                  <Text size={18} weight={600} color={textColor} $textAlign="right">
                                    {isWithdrawal ? amount.toLocaleString() : `+${amount.toLocaleString()}`}원
                                  </Text>
                                </RowBetween>
                              </ColumnItem>
                            </RowBetween>
                            <AmountInfoBox>
                              {(transactionType === 'SALE' || transactionType === 'REFUND') && (
                                <Text size={10}>거래ID: {orderSequence}</Text>
                              )}
                              <AmountDescription>
                                {hasFee && (
                                  <Text size={10} color="text_secondary">
                                    (수수료 포함)
                                  </Text>
                                )}
                                <Text size={10} color="text_secondary">
                                  {balance.toLocaleString()}원
                                </Text>
                              </AmountDescription>
                            </AmountInfoBox>
                          </ColumnItem>
                        </TransactionContainer>
                      </BreakdownContainer>
                    )
                  },
                )}
              </ColumnItem>
            ) : (
              <EmptyBox>
                <Text size={14} lineHeight={22} color="text_secondary" $textAlign="center">
                  조회 가능한 내역이 없습니다.
                </Text>
              </EmptyBox>
            )}
          </>
        )}
      </StyledContent>
    </Wrap>
  )
})

const StyledContent = styled(Content)`
  padding-bottom: 16px;
`

const LoadingWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`

const EmptyBox = styled.div`
  text-align: center;
  width: 100%;
  padding: 120px 0;
`

const AccountInfoWrap = styled.div`
  padding: 16px;
  background-color: ${Color.gray_1};
`

const AccountCard = styled(ColumnItem)`
  gap: 8px;
  padding: 24px;
  border-radius: 16px;
  background-color: ${Color.white};
`

const UserAccountInfo = styled(ColumnItem)`
  margin: 8px 0;
`

const FilterContainer = styled.div`
  position: sticky;
  top: 0;
  padding: 16px 0;
  background-color: ${Color.white};
`

const SortContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-right: 16px;
  padding-bottom: 16px;
`

const BreakdownContainer = styled.div`
  padding: 16px 24px;
  border-bottom: 1px solid ${Color.line};
`

const TransactionContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 32px;
  > *:first-child {
    flex-shrink: 0;
  }
  > *:last-child {
    width: 100%;
  }
`

const AmountInfoBox = styled(RowBetween)`
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  > *:first-child {
    flex-grow: 1;
    width: 100%;
  }
`

const AmountDescription = styled(ColumnItem)`
  flex-grow: 0;
  flex-shrink: 0;
  align-items: flex-end;
  gap: 6px;
`
